import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ReorderRow } from '../../../../applications/ports/versionReportVersionRepository'
import { useVersionReportVersionRepository } from '../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import {
  VersionReportVersionEntity,
  VersionReportVersionPageEntity,
} from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { usePageState } from './pageState'
import { useVersionReportPageDisplayState } from './pageDisplayState'
import { useUrlQuery } from './useUrlQuery'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { getErrorCode } from '../../../../infrastructure/api/error'

export type UseCurrentVersionReturnValue = ReturnType<typeof useCurrentVersion>
export type CurrentReport = {
  versionUuid: string | undefined
  updateVersionUuid: (v: string | undefined) => void
  currentVersion: VersionReportVersionEntity | undefined
  currentPage: VersionReportVersionPageEntity | undefined
}
export const useCurrentVersion = (uuid: string, projectUuid: string) => {
  const [versionUuid, setVersionUuid] = useState<string | undefined>()
  const [currentVersion, setCurrentVersion] = useState<
    VersionReportVersionEntity | undefined
  >()
  const {
    fetch,
    reCalculateSnapshot,
    updatePageRow,
    reorderPageRows,
    updatePageContent,
  } = useVersionReportVersionRepository()
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [initialized, setInitialized] = useState<boolean>(false)
  const dispatch = useDispatch()

  // commit 系が stale closure を避けて最新の currentVersion を読むための ref。
  const currentVersionRef = useRef<VersionReportVersionEntity | undefined>(
    currentVersion
  )
  useEffect(() => {
    currentVersionRef.current = currentVersion
  }, [currentVersion])

  const { versionUuidFromUrl } = useUrlQuery()
  const {
    storedVersionUuid,
    store,
    initialized: pageStateInitialized,
  } = usePageState(uuid, projectUuid)

  const updateVersionUuid = useCallback(
    (newVersionUuid: string | undefined) => {
      setVersionUuid(newVersionUuid)
    },
    []
  )

  const rawCurrentPage = useMemo(() => {
    if (!currentVersion) return undefined
    return currentVersion.pages[currentPageIndex]
  }, [currentPageIndex, currentVersion])

  const {
    displayState,
    updateDisplayState,
    restoreToken: displayStateRestoreToken,
  } = useVersionReportPageDisplayState(uuid, rawCurrentPage?.templatePageUuid)

  const currentPage = rawCurrentPage

  useEffect(() => {
    const fn = async () => {
      if (!versionUuid) {
        setCurrentVersion(undefined)
        setCurrentPageIndex(0)
        const noDataStored = pageStateInitialized && !storedVersionUuid
        noDataStored && setInitialized(true)
        return
      }
      const response = await fetch(versionUuid)
      if (!response) {
        setVersionUuid(undefined)
        setCurrentVersion(undefined)
        setCurrentPageIndex(0)
        // 削除/欠損した報告書の UUID が ui state に残り続けるのを防ぐ
        store({ versionUuid: '' })
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'versionReport.deleted' }),
          })
        )
        pageStateInitialized && setInitialized(true)
        return
      }
      setCurrentVersion(response)
      pageStateInitialized && setInitialized(true)
    }
    fn()
  }, [fetch, versionUuid, pageStateInitialized, store, dispatch])

  useEffect(() => {
    if (!pageStateInitialized) return
    if (versionUuidFromUrl) {
      setVersionUuid(versionUuidFromUrl)
    } else if (storedVersionUuid) {
      setVersionUuid(storedVersionUuid)
    }
  }, [
    pageStateInitialized,
    storedVersionUuid,
    setVersionUuid,
    versionUuidFromUrl,
  ])

  useEffect(() => {
    if (!versionUuid) return
    store({ versionUuid })
  }, [versionUuid, store])

  const updatePage = useCallback(
    <
      K extends keyof VersionReportVersionPageEntity,
      V extends VersionReportVersionPageEntity[K],
    >(
      path: K,
      value: V
    ) => {
      setCurrentVersion(prev => {
        if (!prev) return prev
        const newPages = prev.pages.map((page, index) => {
          if (index === currentPageIndex) {
            const newPage = {
              ...page,
            }
            newPage[path] = value
            return newPage
          }
          return page
        })
        return {
          ...prev,
          pages: newPages,
        }
      })
    },
    [currentPageIndex]
  )
  const updatePageTitle = useCallback(
    (newValue: string) => {
      updatePage('pageTitle', newValue)
    },
    [updatePage]
  )
  const updateLeadSentence = useCallback(
    (newValue: string) => {
      updatePage('leadSentence', newValue)
    },
    [updatePage]
  )
  const toNextPage = useCallback(() => {
    if (!currentVersion) return
    setCurrentPageIndex(prev => {
      if (prev === currentVersion.pages.length - 1) {
        return 0
      }
      return prev + 1
    })
  }, [currentVersion])
  const toPrevPage = useCallback(() => {
    if (!currentVersion) return
    setCurrentPageIndex(prev => {
      if (prev === 0) {
        return currentVersion.pages.length - 1
      }
      return prev - 1
    })
  }, [currentVersion])
  const reCalculateAndFetchVersion = useCallback(async () => {
    if (!versionUuid) return
    await reCalculateSnapshot(versionUuid)
    const response = await fetch(versionUuid)
    if (!response) {
      setVersionUuid(undefined)
      setCurrentVersion(undefined)
      setCurrentPageIndex(0)
      return
    }
    setCurrentVersion(response)
  }, [versionUuid, reCalculateSnapshot, fetch])

  const updateCurrentVersion = useCallback(
    (version: VersionReportVersionEntity) => {
      setCurrentVersion(version)
      setCurrentPageIndex(prev =>
        prev >= version.pages.length
          ? Math.max(0, version.pages.length - 1)
          : prev
      )
    },
    []
  )

  const refetchVersion = useCallback(async () => {
    if (!versionUuid) return
    const response = await fetch(versionUuid)
    if (response) {
      updateCurrentVersion(response)
    }
  }, [versionUuid, fetch, updateCurrentVersion])

  const isHandlingConflictRef = useRef(false)
  const handleSaveConflict = useCallback(async () => {
    if (isHandlingConflictRef.current) return
    isHandlingConflictRef.current = true
    try {
      const version = currentVersionRef.current
      if (version) {
        const latest = await fetch(version.uuid)
        if (latest) updateCurrentVersion(latest)
      }
      dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'versionReport.conflict.title' }),
          text: intl.formatMessage({ id: 'versionReport.conflict.message' }),
        })
      )
    } finally {
      isHandlingConflictRef.current = false
    }
  }, [fetch, updateCurrentVersion, dispatch])

  const updateRow = useCallback(
    async (
      rowUuid: string,
      lockVersion: number,
      key: 'status' | 'comment',
      value: string
    ): Promise<boolean> => {
      try {
        const response = await updatePageRow({
          rowUuid,
          lockVersion,
          key,
          value,
        })
        setCurrentVersion(prev => {
          if (!prev) return prev
          return {
            ...prev,
            pages: prev.pages.map(page => {
              if (!page.rows.some(r => r.uuid === rowUuid)) return page
              return {
                ...page,
                rows: page.rows.map(row =>
                  row.uuid === rowUuid
                    ? {
                        ...row,
                        [key]: value,
                        lockVersion: response.lockVersion,
                      }
                    : row
                ),
              }
            }),
          }
        })
        return true
      } catch (e) {
        if (getErrorCode(e) === 'CONSISTENCY_VIOLATION') {
          await handleSaveConflict()
          return false
        }
        throw e
      }
    },
    [updatePageRow, handleSaveConflict]
  )

  const applyReorderedRows = useCallback(
    async (pageUuid: string, reorderPayload: ReorderRow[]) => {
      try {
        const response = await reorderPageRows({ rows: reorderPayload })
        const newLockVersionByRowUuid = new Map(
          response.rows.map(r => [r.uuid, r.lockVersion])
        )
        const newDisplayOrderByRowUuid = new Map(
          reorderPayload.map(p => [p.rowUuid, p.displayOrder])
        )
        setCurrentVersion(prev => {
          if (!prev) return prev
          return {
            ...prev,
            pages: prev.pages.map(page => {
              if (page.uuid !== pageUuid) return page
              const updatedRows = page.rows
                .map(row => {
                  const newLockVersion = newLockVersionByRowUuid.get(row.uuid)
                  const newDisplayOrder = newDisplayOrderByRowUuid.get(row.uuid)
                  return newLockVersion !== undefined &&
                    newDisplayOrder !== undefined
                    ? {
                        ...row,
                        lockVersion: newLockVersion,
                        displayOrder: newDisplayOrder,
                      }
                    : row
                })
                .sort((a, b) => a.displayOrder - b.displayOrder)
              return { ...page, rows: updatedRows }
            }),
          }
        })
      } catch (e) {
        if (getErrorCode(e) === 'CONSISTENCY_VIOLATION') {
          await handleSaveConflict()
        } else {
          throw e
        }
      }
    },
    [reorderPageRows, handleSaveConflict]
  )

  const reflectPageLockVersion = useCallback(
    (pageUuid: string, lockVersion: number) => {
      setCurrentVersion(prev => {
        if (!prev) return prev
        return {
          ...prev,
          pages: prev.pages.map(page =>
            page.uuid === pageUuid ? { ...page, lockVersion } : page
          ),
        }
      })
    },
    []
  )

  // overrides は入力欄の最終値 (debounce 未反映分があるため ref ではなく直接受け取る)。
  const commitPageContent = useCallback(
    async (
      pageUuid: string,
      overrides?: { pageTitle?: string; leadSentence?: string | undefined }
    ): Promise<boolean> => {
      const version = currentVersionRef.current
      if (!version) return false
      const page = version.pages.find(p => p.uuid === pageUuid)
      if (!page) return false
      const pageTitle = overrides?.pageTitle ?? page.pageTitle
      const leadSentence =
        overrides && 'leadSentence' in overrides
          ? overrides.leadSentence
          : page.leadSentence
      try {
        const res = await updatePageContent({
          versionPageUuid: pageUuid,
          lockVersion: page.lockVersion,
          pageTitle,
          leadSentence,
        })
        reflectPageLockVersion(pageUuid, res.lockVersion)
        return true
      } catch (e) {
        if (getErrorCode(e) === 'CONSISTENCY_VIOLATION') {
          await handleSaveConflict()
          return false
        }
        throw e
      }
    },
    [updatePageContent, reflectPageLockVersion, handleSaveConflict]
  )

  return {
    versionUuid,
    updateVersionUuid,
    currentPage,
    currentPageIndex,
    currentVersion,
    updateCurrentVersion,
    updatePageTitle,
    updateLeadSentence,
    updateRow,
    applyReorderedRows,
    commitPageContent,
    toNextPage,
    toPrevPage,
    reCalculateAndFetchVersion,
    refetchVersion,
    storedVersionUuid,
    initialized,
    displayState,
    updateDisplayState,
    displayStateRestoreToken,
  }
}
